import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import API_URL from '../../config/api';
import { FLAGS } from '../../lib/flags';

/**
 * DataHealthDashboard - Debug dashboard for data audit metrics
 * Route: /debug/data-health
 * Gated by: FEATURE_DEBUG_DASHBOARD env flag or admin check
 */
export default function DataHealthDashboard() {
  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('overview');
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [itemStats, setItemStats] = useState(null);

  useEffect(() => {
    loadAuditMetrics();
    loadItemStats();
  }, []);

  const loadItemStats = async () => {
    try {
      const { data: items, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id, data_confidence', { count: 'exact', head: true });

      if (error) throw error;

      // Get counts
      const { count: total } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*', { count: 'exact', head: true });

      const { count: withConfidence } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*', { count: 'exact', head: true })
        .not('data_confidence', 'is', null);

      const { count: withoutConfidence } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*', { count: 'exact', head: true })
        .is('data_confidence', null);

      setItemStats({
        total: total || 0,
        withConfidence: withConfidence || 0,
        withoutConfidence: withoutConfidence || 0,
      });
    } catch (err) {
      console.error('Failed to load item stats', err);
    }
  };

  const handleBackfill = async (batchSize = 10) => {
    setIsBackfilling(true);
    setBackfillStatus(null);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const baseUrl = API_URL.replace('/v1', '') || 'http://localhost:4028';
      
      const response = await fetch(`${baseUrl}/v1/admin/backfill-confidence`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ batchSize }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backfill failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      // Handle double-wrapping if needed
      const actualResult = result.success && result.data ? result.data : result;
      
      setBackfillStatus({
        processed: actualResult.processed || 0,
        errors: actualResult.errors || 0,
        timestamp: new Date().toISOString(),
      });

      // Reload stats and audit metrics
      await loadItemStats();
      await loadAuditMetrics();
    } catch (err) {
      setError(err.message);
      console.error('Backfill error', err);
    } finally {
      setIsBackfilling(false);
    }
  };

  const loadAuditMetrics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }
      
      if (!session || !session.access_token) {
        throw new Error('Not authenticated. Please log in to view audit metrics.');
      }

      const baseUrl = API_URL.replace('/v1', '') || 'http://localhost:4028';
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${baseUrl}/v1/admin/audit-metrics`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to load audit metrics: ${response.status} ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          // If response isn't JSON, use the text
          if (errorText) {
            errorMessage += ` - ${errorText.substring(0, 100)}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      console.log('Audit metrics response:', { 
        success: result.success, 
        hasData: !!result.data,
        resultKeys: Object.keys(result),
        dataKeys: result.data ? Object.keys(result.data) : [],
        timestamp: result.data?.timestamp
      });
      
      // Extract the actual snapshot data
      // ResponseInterceptor wraps as {success: true, data: snapshot, timestamp, path}
      // But if snapshot itself has {success, data}, we need to unwrap it
      let snapshotData = null;
      if (result.success && result.data) {
        snapshotData = result.data;
        // Check if result.data is itself wrapped (has success and data keys)
        if (snapshotData.success && snapshotData.data) {
          // Double-wrapped, unwrap it
          console.log('Detected double-wrapping, unwrapping...');
          snapshotData = snapshotData.data;
        }
      } else if (result.data) {
        snapshotData = result.data;
        // Check if result.data is itself wrapped
        if (snapshotData.success && snapshotData.data) {
          snapshotData = snapshotData.data;
        }
      } else if (result.timestamp && result.embeddingAccuracy) {
        // Snapshot is at root level (shouldn't happen with ResponseInterceptor, but handle it)
        snapshotData = result;
      } else {
        console.error('Invalid response format:', result);
        throw new Error('Invalid response format from server: ' + JSON.stringify(result).substring(0, 200));
      }
      
      console.log('Setting snapshot data:', {
        hasEmbeddingAccuracy: !!snapshotData.embeddingAccuracy,
        hasTagQuality: !!snapshotData.tagQuality,
        hasTimestamp: !!snapshotData.timestamp,
        hasLastBackfill: !!snapshotData.lastBackfill,
        keys: Object.keys(snapshotData)
      });
      
      // Extract last backfill status if available
      if (snapshotData.lastBackfill) {
        setBackfillStatus({
          processed: snapshotData.lastBackfill.processed || 0,
          errors: snapshotData.lastBackfill.errors || 0,
          timestamp: snapshotData.lastBackfill.timestamp,
        });
      }
      
      setSnapshot(snapshotData);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load audit metrics', err);
      
      let errorMessage = 'Failed to load audit metrics';
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. The server may be slow or unresponsive.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadAuditMetrics}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="border rounded-lg p-6 bg-yellow-50 border-yellow-200">
          <h2 className="text-xl font-semibold mb-2">⏳ Loading Audit Data...</h2>
          <p className="text-yellow-800">Fetching audit metrics from server...</p>
          <button
            onClick={() => setIsLoading(false)}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="border rounded-lg p-6 bg-red-50 border-red-200">
          <h2 className="text-xl font-semibold mb-2">❌ Error Loading Audit Data</h2>
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadAuditMetrics}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no snapshot
  if (!snapshot) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="border rounded-lg p-6 bg-yellow-50 border-yellow-200">
          <h2 className="text-xl font-semibold mb-2">⏳ No Audit Data</h2>
          <p className="text-yellow-800">No audit data available. Click the button below to load metrics.</p>
          <button
            onClick={loadAuditMetrics}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Load Audit Metrics
          </button>
        </div>
      </div>
    );
  }

  // If snapshot exists but missing embeddingAccuracy, show what we have
  if (!snapshot.embeddingAccuracy) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="border rounded-lg p-6 bg-yellow-50 border-yellow-200">
          <h2 className="text-xl font-semibold mb-2">⚠️ Partial Audit Data</h2>
          <p className="text-yellow-800">
            Audit data received but missing some metrics. Available keys: {Object.keys(snapshot).join(', ')}
          </p>
          <button
            onClick={loadAuditMetrics}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Data Health Dashboard</h1>
          <p className="text-gray-600">
            Audit metrics snapshot from {new Date(snapshot.timestamp).toLocaleString()}
          </p>
        </div>
        
        {/* Confidence Score Status Section - Transparency, Not Action */}
        <div className="bg-white border rounded-lg p-4 shadow-sm min-w-[300px]">
          <h3 className="font-semibold mb-2">Confidence Scores</h3>
          
          {/* Status Display - Shows transparency */}
          {itemStats && (
            <div className="text-sm space-y-2 mb-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total items:</span>
                <span className="font-medium">{itemStats.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">With scores:</span>
                <span className="font-medium text-green-600">{itemStats.withConfidence}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pending:</span>
                <span className={`font-medium ${itemStats.withoutConfidence > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {itemStats.withoutConfidence}
                </span>
              </div>
              <div className="pt-2 border-t text-xs text-gray-500">
                <div>🔄 Auto-refresh: Nightly at 2 AM</div>
                <div>📅 Deep recalculation: Sundays at 3 AM</div>
              </div>
            </div>
          )}
          
          {/* Last Backfill Status */}
          {backfillStatus && (
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="text-blue-800 font-medium">
                Last refresh: {new Date(backfillStatus.timestamp).toLocaleString()}
              </div>
              <div className="text-blue-700 text-xs mt-1">
                Processed {backfillStatus.processed} items
                {backfillStatus.errors > 0 && ` • ${backfillStatus.errors} errors`}
              </div>
            </div>
          )}
          
          {/* Admin-Only Emergency Trigger (Hidden by default) */}
          {(import.meta.env.DEV || import.meta.env.VITE_FEATURE_DEBUG_DASHBOARD) && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                Admin: Emergency Recalculate
              </summary>
              <div className="mt-2 pt-2 border-t">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBackfill(10)}
                    disabled={isBackfilling}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      isBackfilling
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {isBackfilling ? 'Processing...' : 'Recalc (10)'}
                  </button>
                  <button
                    onClick={() => handleBackfill(50)}
                    disabled={isBackfilling}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      isBackfilling
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {isBackfilling ? 'Processing...' : 'Recalc (50)'}
                  </button>
                </div>
                <p className="text-gray-400 mt-1">
                  Only use if automatic jobs fail
                </p>
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Metric Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {['overview', 'embeddings', 'tags', 'ocr', 'sentiment', 'clusters', 'confidence'].map((metric) => (
          <button
            key={metric}
            onClick={() => setSelectedMetric(metric)}
            className={`px-4 py-2 font-medium ${
              selectedMetric === metric
                ? 'border-b-2 border-[#7A47F5] text-[#7A47F5]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {metric.charAt(0).toUpperCase() + metric.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview */}
      {selectedMetric === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <MetricCard
            title="Embedding Accuracy"
            value={`${(snapshot.embeddingAccuracy.accuracy * 100).toFixed(1)}%`}
            target="≥85%"
            status={snapshot.embeddingAccuracy.accuracy >= 0.85 ? 'pass' : 'fail'}
            subtitle={`Sample: ${snapshot.embeddingAccuracy.sampleSize} items`}
          />
          <MetricCard
            title="Tag Precision"
            value={`${(snapshot.tagQuality.precision * 100).toFixed(1)}%`}
            target="≥80%"
            status={snapshot.tagQuality.precision >= 0.8 ? 'pass' : 'fail'}
            subtitle={`F1: ${(snapshot.tagQuality.f1 * 100).toFixed(1)}%`}
          />
          <MetricCard
            title="Tag Recall"
            value={`${(snapshot.tagQuality.recall * 100).toFixed(1)}%`}
            target="≥75%"
            status={snapshot.tagQuality.recall >= 0.75 ? 'pass' : 'fail'}
            subtitle={`Sample: ${snapshot.tagQuality.sampleSize} items`}
          />
          <MetricCard
            title="OCR CER"
            value={`${(snapshot.ocrMetrics.characterErrorRate * 100).toFixed(1)}%`}
            target="<10%"
            status={snapshot.ocrMetrics.characterErrorRate < 0.1 ? 'pass' : 'fail'}
            subtitle={`WER: ${(snapshot.ocrMetrics.wordErrorRate * 100).toFixed(1)}%`}
          />
          <MetricCard
            title="Sentiment Agreement"
            value={`${(snapshot.sentimentAgreement.agreement * 100).toFixed(1)}%`}
            target="≥80%"
            status={snapshot.sentimentAgreement.agreement >= 0.8 ? 'pass' : 'fail'}
            subtitle={`Within 1pt: ${(snapshot.sentimentAgreement.withinOnePoint * 100).toFixed(1)}%`}
          />
          <MetricCard
            title="Avg Cluster Silhouette"
            value={snapshot.clusterCoherence.averageSilhouette.toFixed(2)}
            target="≥0.5"
            status={snapshot.clusterCoherence.averageSilhouette >= 0.5 ? 'pass' : 'fail'}
            subtitle={`Diversity: ${(snapshot.clusterCoherence.averageDiversity * 100).toFixed(1)}%`}
          />
        </div>
      )}

      {/* Embedding Accuracy */}
      {selectedMetric === 'embeddings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Embedding Accuracy Distribution</h2>
            <AccuracyHistogram distribution={snapshot.embeddingAccuracy.distribution} />
            {snapshot.embeddingAccuracy.outliers.length > 0 && (
              <div className="mt-4">
                <h3 className="font-medium mb-2">Outliers ({snapshot.embeddingAccuracy.outliers.length})</h3>
                <div className="space-y-2">
                  {snapshot.embeddingAccuracy.outliers.slice(0, 10).map((outlier, idx) => (
                    <div key={idx} className="text-sm text-gray-600">
                      <span className="font-mono">{outlier.itemId}</span>: {outlier.reason} ({(outlier.accuracy * 100).toFixed(1)}%)
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tag Quality */}
      {selectedMetric === 'tags' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Tag Quality Metrics</h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-600">Precision</div>
                <div className="text-2xl font-bold">{((snapshot.tagQuality.precision * 100).toFixed(1))}%</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Recall</div>
                <div className="text-2xl font-bold">{((snapshot.tagQuality.recall * 100).toFixed(1))}%</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">F1 Score</div>
                <div className="text-2xl font-bold">{((snapshot.tagQuality.f1 * 100).toFixed(1))}%</div>
              </div>
            </div>
            {snapshot.tagQuality.failureModes.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Failure Modes</h3>
                {snapshot.tagQuality.failureModes.map((mode, idx) => (
                  <div key={idx} className="mb-2">
                    <span className="font-medium">{mode.type}</span>: {mode.count} items
                    {mode.examples.length > 0 && (
                      <div className="text-sm text-gray-600 ml-4">
                        Examples: {mode.examples.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* OCR Metrics */}
      {selectedMetric === 'ocr' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">OCR Error Rates</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-600">Character Error Rate (CER)</div>
                <div className="text-2xl font-bold">{((snapshot.ocrMetrics.characterErrorRate * 100).toFixed(1))}%</div>
                <div className="text-xs text-gray-500">Target: &lt;10%</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Word Error Rate (WER)</div>
                <div className="text-2xl font-bold">{((snapshot.ocrMetrics.wordErrorRate * 100).toFixed(1))}%</div>
                <div className="text-xs text-gray-500">Target: &lt;15%</div>
              </div>
            </div>
            {snapshot.ocrMetrics.samples.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Sample Errors</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {snapshot.ocrMetrics.samples.slice(0, 10).map((sample, idx) => (
                    <div key={idx} className="text-sm border-b pb-2">
                      <div className="font-mono text-xs text-gray-500">{sample.itemId}</div>
                      <div className="mt-1">
                        <div className="text-green-700">GT: {sample.groundTruth.substring(0, 50)}...</div>
                        <div className="text-red-700">Ext: {sample.extracted.substring(0, 50)}...</div>
                        <div className="text-gray-600">CER: {(sample.cer * 100).toFixed(1)}%, WER: {(sample.wer * 100).toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sentiment Agreement */}
      {selectedMetric === 'sentiment' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Sentiment Agreement</h2>
            <div className="mb-4">
              <div className="text-sm text-gray-600">Agreement</div>
              <div className="text-2xl font-bold">{((snapshot.sentimentAgreement.agreement * 100).toFixed(1))}%</div>
              <div className="text-xs text-gray-500">Within 1 point: {(snapshot.sentimentAgreement.withinOnePoint * 100).toFixed(1)}%</div>
            </div>
            {snapshot.sentimentAgreement.heatmap.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Agreement Heatmap</h3>
                <SentimentHeatmap data={snapshot.sentimentAgreement.heatmap} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cluster Coherence */}
      {selectedMetric === 'clusters' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Cluster Coherence</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-600">Avg Silhouette</div>
                <div className="text-2xl font-bold">{snapshot.clusterCoherence.averageSilhouette.toFixed(2)}</div>
                <div className="text-xs text-gray-500">Target: ≥0.5</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Avg Diversity</div>
                <div className="text-2xl font-bold">{(snapshot.clusterCoherence.averageDiversity * 100).toFixed(1)}%</div>
                <div className="text-xs text-gray-500">Target: 60-80%</div>
              </div>
            </div>
            <ClusterScatterPlot clusters={snapshot.clusterCoherence.clusters} />
            {snapshot.clusterCoherence.outliers.length > 0 && (
              <div className="mt-4">
                <h3 className="font-medium mb-2">Outlier Clusters</h3>
                {snapshot.clusterCoherence.outliers.map((outlier, idx) => (
                  <div key={idx} className="text-sm text-gray-600 mb-1">
                    <span className="font-mono">{outlier.clusterId}</span>: {outlier.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confidence Distribution */}
      {selectedMetric === 'confidence' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Data Confidence Distribution</h2>
            <div className="grid grid-cols-5 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-600">Mean</div>
                <div className="text-xl font-bold">{snapshot.dataConfidenceDistribution.mean.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Median</div>
                <div className="text-xl font-bold">{snapshot.dataConfidenceDistribution.median.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">P25</div>
                <div className="text-xl font-bold">{snapshot.dataConfidenceDistribution.p25.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">P75</div>
                <div className="text-xl font-bold">{snapshot.dataConfidenceDistribution.p75.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">P95</div>
                <div className="text-xl font-bold">{snapshot.dataConfidenceDistribution.p95.toFixed(2)}</div>
              </div>
            </div>
            <ConfidenceHistogram distribution={snapshot.dataConfidenceDistribution} />
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components

function MetricCard({ title, value, target, status, subtitle }) {
  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">{title}</h3>
        <span className={`px-2 py-1 rounded text-xs ${
          status === 'pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {status === 'pass' ? '✓' : '✗'}
        </span>
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm text-gray-600">Target: {target}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function AccuracyHistogram({ distribution }) {
  const max = Math.max(distribution.excellent, distribution.good, distribution.fair, distribution.poor);
  
  return (
    <div className="space-y-2">
      {[
        { label: 'Excellent (>90%)', value: distribution.excellent, color: 'bg-green-500' },
        { label: 'Good (70-90%)', value: distribution.good, color: 'bg-blue-500' },
        { label: 'Fair (50-70%)', value: distribution.fair, color: 'bg-yellow-500' },
        { label: 'Poor (<50%)', value: distribution.poor, color: 'bg-red-500' },
      ].map((item, idx) => (
        <div key={idx} className="flex items-center gap-4">
          <div className="w-24 text-sm">{item.label}</div>
          <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
            <div
              className={`${item.color} h-full transition-all`}
              style={{ width: `${max > 0 ? (item.value / max) * 100 : 0}%` }}
            />
          </div>
          <div className="w-12 text-sm text-right">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function SentimentHeatmap({ data }) {
  // Create 5x5 grid for sentiment scores 1-5
  const grid = Array(5).fill(null).map(() => Array(5).fill(0));
  
  data.forEach(({ manual, extracted, count }) => {
    if (manual >= 1 && manual <= 5 && extracted >= 1 && extracted <= 5) {
      grid[manual - 1][extracted - 1] += count;
    }
  });

  const maxCount = Math.max(...grid.flat());

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-6 gap-1 text-xs">
        <div></div>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="text-center font-medium">{i}</div>
        ))}
      </div>
      {grid.map((row, i) => (
        <div key={i} className="grid grid-cols-6 gap-1">
          <div className="text-xs font-medium text-right pr-2">{i + 1}</div>
          {row.map((count, j) => (
            <div
              key={j}
              className="h-8 rounded flex items-center justify-center text-xs"
              style={{
                backgroundColor: count > 0
                  ? `rgba(124, 58, 237, ${count / maxCount})`
                  : '#f3f4f6',
                color: count > 0 && count / maxCount > 0.5 ? 'white' : 'black',
              }}
            >
              {count > 0 ? count : ''}
            </div>
          ))}
        </div>
      ))}
      <div className="text-xs text-gray-500 mt-2">
        Rows: Manual, Columns: Extracted
      </div>
    </div>
  );
}

function ClusterScatterPlot({ clusters }) {
  return (
    <div className="mt-4">
      <h3 className="font-medium mb-2">Silhouette vs Diversity</h3>
      <div className="relative h-64 border rounded p-4">
        {clusters.map((cluster, idx) => (
          <div
            key={idx}
            className="absolute w-3 h-3 rounded-full bg-[#7A47F5] opacity-60"
            style={{
              left: `${cluster.diversity * 100}%`,
              bottom: `${cluster.silhouetteScore * 100}%`,
            }}
            title={`Cluster ${cluster.clusterId}: S=${cluster.silhouetteScore.toFixed(2)}, D=${(cluster.diversity * 100).toFixed(1)}%`}
          />
        ))}
        <div className="absolute top-0 left-0 text-xs text-gray-500">Diversity →</div>
        <div className="absolute bottom-0 left-0 text-xs text-gray-500 rotate-[-90deg] origin-left">Silhouette ↑</div>
      </div>
    </div>
  );
}

function ConfidenceHistogram({ distribution }) {
  // Simplified histogram visualization
  return (
    <div className="mt-4">
      <h3 className="font-medium mb-2">Distribution</h3>
      <div className="space-y-1">
        {[
          { label: '0.0-0.2', value: 0.1 },
          { label: '0.2-0.4', value: 0.3 },
          { label: '0.4-0.6', value: 0.5 },
          { label: '0.6-0.8', value: 0.7 },
          { label: '0.8-1.0', value: 0.9 },
        ].map((range, idx) => {
          // Estimate distribution based on percentiles
          const estimated = range.value <= distribution.median ? 0.3 : 0.2;
          return (
            <div key={idx} className="flex items-center gap-4">
              <div className="w-16 text-sm">{range.label}</div>
              <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-[#7A47F5] h-full"
                  style={{ width: `${estimated * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

